import { useContext, useState } from 'react'

import { Box, Typography } from '@material-ui/core'
import { DataGridPro, GridRowParams, GridSortDirection, GridSortModel } from '@mui/x-data-grid-pro'
import { ConfirmationDialog } from '@scoir/modals'
import theme from 'material/theme'
import { useDispatch } from 'react-redux'

import EmptyState from '../EmptyState'
import MarketingEmptyState from '../EmptyState/MarketingEmptyState'
import OutreachMessageContext from '../OutreachMessageProvider/Context'
import PreviewModal from '../PreviewModal'
import { Message, PAGE_SIZE_OPTIONS } from '../types'
import getColumns from './columns'
import { onDeleteMessage } from './message-actions'

export default function MessageTableV2({
  messages,
  hasMessages,
  onChange,
  route,
  studentOutreachEnabledOrShadow,
  supportsLimitedAutomatedOutreach,
}: {
  messages: Message[]
  hasMessages: boolean
  onChange: () => void
  route: 'automated' | 'one-time'
  studentOutreachEnabledOrShadow: boolean
  supportsLimitedAutomatedOutreach: boolean
}) {
  const columnDispatch = useDispatch()

  const [previewMessage, setPreviewMessage] = useState<Message | undefined>(undefined)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const { state, dispatch } = useContext(OutreachMessageContext)

  const displayPreview = (params: GridRowParams<Message>) => {
    const selectedMessage = messages.find((msg) => msg.Id === params?.row?.Id)
    setPreviewMessage(selectedMessage)
  }

  // Display the marketing empty state if there are no messages AND Limited Automated Outreach is enabled
  // since the user can't take any action.
  if (!hasMessages && supportsLimitedAutomatedOutreach) {
    return <MarketingEmptyState />
  }
  // Display empty state if there are no messages AND student outreach is enabled
  // (or user is shadow) since they can create messages.
  if (!hasMessages && studentOutreachEnabledOrShadow) {
    return <EmptyState />
  }

  return (
    <Box>
      <DataGridPro
        sx={{
          '& .disabled-row *': {
            color: theme.palette.text.disabled,
          },
        }}
        autoHeight
        onRowClick={displayPreview}
        getRowClassName={(params) => (params.row.Disabled ? `disabled-row` : '')}
        disableColumnReorder
        columns={getColumns({
          route,
          onChange,
          onDelete: setDeleteId,
          dispatch: columnDispatch,
          studentOutreachEnabledOrShadow,
        })}
        rows={messages}
        sortModel={[
          {
            field:
              state?.activeView?.[state.route]?.sortModel?.field ||
              (state.route === 'one-time' ? 'ScheduledDate' : 'CreatedDate'),
            sort: state?.activeView?.[state.route]?.sortModel?.sort as GridSortDirection,
          },
        ]}
        onSortModelChange={(model: GridSortModel) => {
          dispatch({ type: 'update-column-sort', sortModel: model[0] })
        }}
        sortingOrder={['desc', 'asc']}
        getRowId={(row) => row.Id}
        disableColumnFilter
        disableColumnMenu
        disableSelectionOnClick
        pagination
        pageSize={state.pageSize}
        onPageSizeChange={(size) => {
          dispatch({ type: 'pageSize-change', pageSize: size })
        }}
        page={state.currentPage}
        onPageChange={(page) => {
          dispatch({ type: 'page-change', page })
        }}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
      />
      {previewMessage && (
        <PreviewModal
          show={Boolean(previewMessage)}
          outreachItem={previewMessage}
          onClose={() => setPreviewMessage(undefined)}
        />
      )}
      {Boolean(deleteId) && (
        <ConfirmationDialog
          open={Boolean(deleteId)}
          headerText="Delete message?"
          confirmationText="Confirm Delete"
          onConfirm={() => {
            onDeleteMessage({
              message: messages.find((msg) => msg.Id === deleteId)!,
              onChange,
              onCloseDeleteModal: () => setDeleteId(null),
              dispatch,
            })
          }}
          onClose={() => setDeleteId(null)}
        >
          <Typography>This action cannot be undone.</Typography>
        </ConfirmationDialog>
      )}
    </Box>
  )
}
